import type { ReactNode } from "react";
import { HelpCircle } from "@untitledui/icons";
import { Tooltip, TooltipTrigger } from "../components/base/tooltip/tooltip";
import { GLOSSARY, type GlossaryTerm } from "../lib/glossary";
import { cx } from "../utils/cx";

export interface HelpTipProps {
    /** Glossary key; supplies title and definition. */
    term?: GlossaryTerm;
    /** Custom title (overrides the glossary). */
    title?: ReactNode;
    /** Custom description (overrides the glossary). */
    description?: ReactNode;
    /**
     * Inline text to underline as the trigger, e.g. «سرویس فعال».
     * When omitted, a small help icon is the trigger.
     */
    children?: ReactNode;
    className?: string;
}

/** Glossary tooltip (spec §14.6.6) on Untitled UI's Tooltip. Keyboard focusable. */
export const HelpTip = ({ term, title, description, children, className }: HelpTipProps) => {
    const entry = term ? GLOSSARY[term] : undefined;
    const tipTitle = title ?? entry?.term ?? "";
    const tipDescription = description ?? entry?.definition;

    return (
        <Tooltip title={tipTitle} description={tipDescription} placement="top" arrow>
            <TooltipTrigger
                aria-label={typeof tipTitle === "string" ? `توضیح: ${tipTitle}` : "توضیح"}
                className={cx(
                    "inline-flex cursor-help items-center gap-1 align-middle text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover",
                    children && "text-inherit underline decoration-dotted decoration-1 underline-offset-4",
                    className,
                )}
            >
                {children ?? <HelpCircle className="size-4 stroke-[2.25px]" aria-hidden="true" />}
            </TooltipTrigger>
        </Tooltip>
    );
};
